import { isCharacterFeature } from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";

import { CHAT_SIZE_CATEGORY_VALUE_SET } from "@/components/search/chatSizeCategoryValues";
import { DEFINITION_SIZE_CATEGORY_VALUE_SET } from "@/components/search/definitionSizeCategoryValues";
import {
  CHAT_SIZE_CATEGORY,
  DEFINITION_SIZE_CATEGORY,
  FEATURES_CATEGORY,
  TAGS_CATEGORY,
  TRANSLATED_LANGUAGES_CATEGORY,
} from "@/components/search/FilterBox/constants";

import { getBaseRefinementValue, resolveTagConflicts, stripNegativeValues } from "./filterSanitizers";

const logger = appLogger.withTag("parseInstantSearchUrlState");

type ParsedSearchValue = Record<string, unknown>;

interface IQsParseOptions {
  arrayLimit?: number;
}

interface IQsModule {
  parse: (search: string, options?: IQsParseOptions) => unknown;
}

const isObjectRecord = (value: unknown): value is ParsedSearchValue => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

const isNumericKeyedRecord = (value: ParsedSearchValue) => {
  const keys = Object.keys(value);

  return keys.length > 0 && keys.every((key) => /^\d+$/.test(key));
};

const normalizeRefinementValue = (value: unknown): unknown => {
  if (typeof value === "string") {
    return [value];
  }

  if (isObjectRecord(value) && isNumericKeyedRecord(value)) {
    return Object.entries(value)
      .sort(([left], [right]) => Number(left) - Number(right))
      .map(([, nestedValue]) => nestedValue);
  }

  return value;
};

/**
 * Maps allowlist-restricted attributes to their valid value sets.
 * Any value not in the set (including negated `-` prefixed values) is stripped.
 */
const ALLOWLISTED_ATTRIBUTES: Record<string, ReadonlySet<string>> = {
  [DEFINITION_SIZE_CATEGORY]: DEFINITION_SIZE_CATEGORY_VALUE_SET,
  [CHAT_SIZE_CATEGORY]: CHAT_SIZE_CATEGORY_VALUE_SET,
};

const sanitizeRefinementValues = (attribute: string, values: unknown): unknown => {
  if (!Array.isArray(values)) {
    return values;
  }

  const stringValues = values.filter((value): value is string => typeof value === "string");

  const allowedValues = ALLOWLISTED_ATTRIBUTES[attribute];
  if (allowedValues) {
    return stringValues.filter((value) => allowedValues.has(value));
  }

  if (attribute === TRANSLATED_LANGUAGES_CATEGORY) {
    return stripNegativeValues(stringValues);
  }

  if (attribute === TAGS_CATEGORY) {
    return resolveTagConflicts(stringValues);
  }

  if (attribute === FEATURES_CATEGORY) {
    return resolveTagConflicts(stringValues.filter((value) => isCharacterFeature(getBaseRefinementValue(value))));
  }

  return stringValues;
};

const normalizeRefinementList = (value: unknown): unknown => {
  if (!isObjectRecord(value)) {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, refinementValue]) => [key, sanitizeRefinementValues(key, normalizeRefinementValue(refinementValue))]),
  );
};

const normalizeIndexUiState = (value: unknown): unknown => {
  if (!isObjectRecord(value)) {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, nestedValue]) => [key, key === "refinementList" ? normalizeRefinementList(nestedValue) : nestedValue]),
  );
};

// eslint-disable-next-line import/prefer-default-export -- Call sites use named import; default would force a wide refactor
export function parseInstantSearchUrlState(qsModule: IQsModule, search: string) {
  try {
    const parsed = qsModule.parse(search.startsWith("?") ? search.slice(1) : search, { arrayLimit: 99 });

    if (!isObjectRecord(parsed)) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(parsed)
        .filter(([, value]) => isObjectRecord(value))
        .map(([key, value]) => [key, normalizeIndexUiState(value)]),
    );
  } catch (error) {
    logger.error("Error parsing URL", error);
    return {};
  }
}
